import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  CircleAlert,
  CreditCard,
  ExternalLink,
  Globe2,
  HeartPulse,
  Languages,
  MapPinned,
  PiggyBank,
  PlayCircle,
  ShieldCheck,
  SunMedium,
  Waves,
  Wifi,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { CountryLabel } from "@/components/CountryLabel";
import { CountrySafetySignal } from "@/components/CountrySafetySignal";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import CountryPreviewPage from "@/pages/CountryPreviewPage";
import { getCountryExploreContent } from "@/lib/countryExploreContent";
import { getCountryCostRangePresentation } from "@/lib/currencyDisplay";
import { getCurrentReviewedMonthYear, getReviewedLabel } from "@/lib/countryTrust";
import { buildCountryReasoningMap, filterDistinctIdeaList } from "@/lib/reasoningQuality";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const scorecardIcons = {
  "Affordability": PiggyBank,
  "Healthcare": HeartPulse,
  "Residency Accessibility": BadgeCheck,
  "Lifestyle / Climate": SunMedium,
  "Language / English Ease": Languages,
  "Infrastructure / Internet": Wifi,
};

const priorityLabels = {
  cost_of_living: "cost balance",
  healthcare: "healthcare confidence",
  safety: "stability and predictability",
  climate: "warmer day-to-day living",
  community: "social landing ease",
  infrastructure: "daily infrastructure",
  mobility: "move practicality",
  lifestyle: "quality of life",
};

const formatThemeList = (items = []) => {
  if (!items.length) return "the priorities carrying the most weight in your plan";
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
};

const dedupeList = (items = []) => Array.from(new Set(items.filter(Boolean)));

const getRegionProfileHint = (countryKey, regionName, summary = {}) => {
  const priorities = summary.priorities || [];
  const budget = String(summary.budget || summary.budget_abroad || "").toLowerCase();

  if (countryKey === "portugal") {
    if (["Lisbon", "Porto"].includes(regionName) && (summary.need_reliable_internet || summary.work_abroad_intent === "remote_self_employed" || priorities.includes("healthcare"))) {
      return "Worth a closer look if stronger services, healthcare access, and remote-work practicality matter most in your decision.";
    }

    if (["Silver Coast", "Central Portugal"].includes(regionName) && (priorities.includes("cost_of_living") || /low|medium|budget/.test(budget))) {
      return "Worth a closer look if keeping day-to-day costs lighter matters to you and housing choices will carry more weight here.";
    }

    if (["Algarve", "Madeira"].includes(regionName) && (priorities.includes("climate") || priorities.includes("community"))) {
      return "Especially worth a look if climate, coast, and an easier social start are part of what you want.";
    }

    return "";
  }

  if (countryKey === "spain") {
    if (["Madrid", "Barcelona"].includes(regionName) && (summary.need_reliable_internet || priorities.includes("healthcare"))) {
      return "Worth a closer look if you want stronger healthcare access, better daily infrastructure, and a more connected urban base.";
    }

    if (["Valencia", "Costa del Sol / Málaga"].includes(regionName) && (priorities.includes("climate") || priorities.includes("community"))) {
      return "Especially worth a look if climate, coast, and an easier social start are part of what you want.";
    }

    return "";
  }

  if (countryKey === "mexico") {
    if (["Mexico City"].includes(regionName) && (summary.need_reliable_internet || priorities.includes("healthcare"))) {
      return "Worth a closer look if you want stronger private healthcare, better infrastructure, and a more fully serviced urban base.";
    }

    if (["Mérida", "Lake Chapala / Ajijic"].includes(regionName) && (priorities.includes("community") || /low|medium|budget/.test(budget))) {
      return "Worth a closer look if an easier landing, steadier pace, or more manageable day-to-day costs matter to you.";
    }

    return "";
  }

  if (countryKey === "costa-rica") {
    if (["Central Valley", "Escazú / Santa Ana"].includes(regionName) && (priorities.includes("healthcare") || summary.need_reliable_internet)) {
      return "Worth a closer look if healthcare access, service reliability, and a more practical everyday base matter most in your decision.";
    }

    if (["Guanacaste", "Southern Zone / Uvita"].includes(regionName) && (priorities.includes("climate") || priorities.includes("nature") || priorities.includes("community"))) {
      return "Especially worth a look if weather, greenery, coast, and lifestyle fit matter more to you than maximum convenience.";
    }

    return "";
  }

  if (countryKey === "panama") {
    if (["Panama City"].includes(regionName) && (summary.need_reliable_internet || priorities.includes("healthcare") || priorities.includes("mobility"))) {
      return "Worth a closer look if healthcare access, banking, and day-to-day practicality matter most in your decision.";
    }

    if (["Boquete", "David / Chiriquí"].includes(regionName) && (priorities.includes("community") || priorities.includes("climate") || /low|medium|budget/.test(budget))) {
      return "Worth a closer look if you want a lower-intensity base, cooler climate, or a softer cost-and-pace tradeoff than Panama City.";
    }

    return "";
  }

  if (countryKey === "greece") {
    if (["Athens", "Thessaloniki"].includes(regionName) && (summary.need_reliable_internet || priorities.includes("healthcare"))) {
      return "Worth a closer look if you want stronger year-round services, healthcare access, and day-to-day practicality.";
    }

    if (["Crete", "Peloponnese", "Cyclades / Island Hubs"].includes(regionName) && (priorities.includes("climate") || priorities.includes("community"))) {
      return "Especially worth a look if Mediterranean climate, coastal living, and a slower rhythm are central to what you want.";
    }

    return "";
  }

  return "";
};

const buildCountryWhyAppearedSummary = (countryName, summary = {}, fitReasons = [], tradeoffReasons = [], inPlan = false) => {
  if (countryName === "Portugal") {
    const topThemes = dedupeList((summary.priorities || []).map((priority) => priorityLabels[priority] || priority.replace(/_/g, " "))).slice(0, 3);
    const primarySupport = fitReasons[0] || "Portugal can still offer a strong balance of healthcare, lifestyle, and longer-stay practicality compared with many alternatives.";
    const secondarySupport = fitReasons[1] || "It can make sense if you want a calmer European base without fully giving up infrastructure or regional choice.";
    const caution = tradeoffReasons[0] || "The main caution is that housing choices carry more weight here, and administrative friction can reshape the fit faster than the headline version of Portugal suggests.";

    return {
      summary: topThemes.length
        ? `Portugal appeared because your plan puts real weight on ${formatThemeList(topThemes)}, and Portugal can support that mix without pushing you into a purely lifestyle-first or purely systems-first choice.`
        : "Portugal appeared because it can bring together healthcare access, day-to-day practicality, coastal appeal, and realistic longer-stay relevance in a useful way.",
      supportLines: [primarySupport, secondarySupport],
      caution,
    };
  }

  const topThemes = dedupeList((summary.priorities || []).map((priority) => priorityLabels[priority] || priority.replace(/_/g, " "))).slice(0, 3);
  const primarySupport = fitReasons[0] || `${countryName} may still be worth a closer look if you need a workable balance of lifestyle fit, practical setup, and longer-stay feasibility.`;
  const secondarySupport = fitReasons[1] || `It is worth a closer look if you want to compare ${countryName} against the countries already carrying the most weight in your decision.`;
  const caution = tradeoffReasons[0] || `The main caution is that ${countryName}'s real fit may shift quickly once regional differences, cost pressure, or day-to-day friction are weighed more closely.`;

  return {
    summary: inPlan
      ? topThemes.length
        ? `${countryName} appeared because your plan puts real weight on ${formatThemeList(topThemes)}, and ${countryName} offers enough alignment across those priorities to deserve a closer look.`
        : `${countryName} appeared because it still offers a meaningful balance of fit, feasibility, and day-to-day practicality for your plan.`
      : topThemes.length
        ? `${countryName} may still be worth a closer look if your plan puts real weight on ${formatThemeList(topThemes)}, even if it is not currently at the top of your shortlist.`
        : `${countryName} may still be worth a closer look as you compare lifestyle fit, practical setup, and longer-stay feasibility across your broader shortlist.`,
    supportLines: [primarySupport, secondarySupport],
    caution,
  };
};

const CountryExploreOrPreviewPage = ({ countryKey }) => {
  const [searchParams] = useSearchParams();
  const fullContent = getCountryExploreContent(countryKey);
  const entryMode = searchParams.get("entry") === "plan" ? "plan" : "public";
  const shouldForcePublicExperience = entryMode === "public";
  const [mode, setMode] = useState(fullContent ? (shouldForcePublicExperience ? "preview" : "loading") : "preview");
  const [currentSlide, setCurrentSlide] = useState(0);
  const [plan, setPlan] = useState(null);
  const [safetyProfile, setSafetyProfile] = useState(null);

  useEffect(() => {
    if (!fullContent) {
      setMode("preview");
      return undefined;
    }

    if (shouldForcePublicExperience) {
      setMode("preview");
      setPlan(null);
      return undefined;
    }

    let isMounted = true;

    const checkEligibility = async () => {
      try {
        const userResponse = await axios.get(`${API}/auth/me`, { withCredentials: true });

        if (!isMounted) return;

        if (!userResponse.data?.has_paid) {
          setMode("preview");
          return;
        }

        setMode("full");

        try {
          const planResponse = await axios.get(`${API}/plan`, { withCredentials: true });
          if (!isMounted) return;
          setPlan(planResponse.data);
        } catch (error) {
          if (!isMounted) return;
          setPlan(null);
        }
      } catch (error) {
        if (!isMounted) return;
        setMode("preview");
      }
    };

    checkEligibility();

    return () => {
      isMounted = false;
    };
  }, [countryKey, fullContent, shouldForcePublicExperience]);

  useEffect(() => {
    if (!fullContent) return undefined;

    let isMounted = true;

    const fetchCountrySafety = async () => {
      try {
        const response = await axios.get(`${API}/countries/${fullContent.code}/public-meta`);
        if (!isMounted) return;
        setSafetyProfile(response.data?.safety_profile || null);
      } catch (error) {
        if (!isMounted) return;
        setSafetyProfile(null);
      }
    };

    fetchCountrySafety();

    return () => {
      isMounted = false;
    };
  }, [fullContent]);

  const activeImage = fullContent?.images?.[currentSlide];
  const goToPreviousSlide = () => setCurrentSlide((currentSlide - 1 + fullContent.images.length) % fullContent.images.length);
  const goToNextSlide = () => setCurrentSlide((currentSlide + 1) % fullContent.images.length);

  const currentPlanCountry = useMemo(() => {
    if (!plan?.shortlisted_countries?.length || !fullContent) return null;
    return plan.shortlisted_countries.find((country) => country?.code === fullContent.code || country?.name === fullContent.name) || null;
  }, [fullContent, plan]);

  const hasPlanCountryMatch = Boolean(currentPlanCountry);

  const countryReasoning = useMemo(() => {
    if (!plan?.summary || !currentPlanCountry) {
      return { fitReasons: [], tradeoffReasons: [] };
    }

    const countryKeyForMap = currentPlanCountry.code || currentPlanCountry.name;
    const reasoningMap = buildCountryReasoningMap([currentPlanCountry], plan.summary, { fitLimit: 4, tradeoffLimit: 4 });
    return reasoningMap[countryKeyForMap] || { fitReasons: [], tradeoffReasons: [] };
  }, [currentPlanCountry, plan]);

  const personalizedSummary = useMemo(
    () => buildCountryWhyAppearedSummary(fullContent.name, plan?.summary || {}, countryReasoning.fitReasons, countryReasoning.tradeoffReasons, hasPlanCountryMatch),
    [countryReasoning.fitReasons, countryReasoning.tradeoffReasons, fullContent.name, hasPlanCountryMatch, plan]
  );
  const reviewedLabel = getReviewedLabel();
  const currentReviewedMonthYear = getCurrentReviewedMonthYear();

  const fitBullets = useMemo(
    () => {
      const distinctFitIdeas = filterDistinctIdeaList(
        dedupeList([...countryReasoning.fitReasons, ...fullContent.fitFallback]),
        [personalizedSummary.summary],
        4,
      );

      return distinctFitIdeas.length ? distinctFitIdeas : dedupeList([...countryReasoning.fitReasons, ...fullContent.fitFallback]).slice(0, 4);
    },
    [countryReasoning.fitReasons, fullContent.fitFallback, personalizedSummary.summary]
  );

  const cautionText = useMemo(() => {
    const distinctCaution = filterDistinctIdeaList(
      [personalizedSummary.caution],
      [personalizedSummary.summary, ...fitBullets],
      1,
    )[0];

    if (distinctCaution) return distinctCaution;

    return filterDistinctIdeaList(
      dedupeList([...countryReasoning.tradeoffReasons, ...fullContent.tradeoffFallback]),
      [personalizedSummary.summary, ...fitBullets],
      1,
    )[0] || personalizedSummary.caution;
  }, [countryReasoning.tradeoffReasons, fitBullets, fullContent.tradeoffFallback, personalizedSummary.caution, personalizedSummary.summary]);

  const tradeoffBullets = useMemo(
    () => {
      const distinctTradeoffIdeas = filterDistinctIdeaList(
        dedupeList([...countryReasoning.tradeoffReasons, ...fullContent.tradeoffFallback]),
        [personalizedSummary.summary, ...fitBullets, cautionText],
        4,
      );

      return distinctTradeoffIdeas.length
        ? distinctTradeoffIdeas
        : ["The remaining friction here depends more on your exact city, route, and day-to-day setup than on the country headline alone."];
    },
    [countryReasoning.tradeoffReasons, fullContent.tradeoffFallback, personalizedSummary.summary, fitBullets, cautionText]
  );

  const practicalContextSections = useMemo(
    () => [
      {
        value: "cost",
        title: "Cost of Living Overview",
        testId: "country-explore-cost-section",
        description: "Broad budget ranges, rent pressure, and where the value picture changes.",
      },
      {
        value: "healthcare",
        title: "Healthcare Overview",
        testId: "country-explore-healthcare-section",
        description: "How the public/private reality usually works and what expats should expect.",
      },
      {
        value: "daily-life",
        title: "Daily Life and Lifestyle",
        testId: "country-explore-daily-life-section",
        description: "Pace of life, social texture, language reality, and everyday mobility.",
      },
      {
        value: "infrastructure",
        title: "Internet and Infrastructure",
        testId: "country-explore-infrastructure-section",
        description: "Remote-work practicality, service reliability, and where things feel smoothest.",
      },
      {
        value: "practical-setup",
        title: "Practical Setup",
        testId: "country-explore-practical-setup-section",
        description: "Banking, tax awareness, onboarding friction, and where professional advice may help.",
      },
      {
        value: "recreation",
        title: "Recreation and Everyday Enjoyment",
        testId: "country-explore-recreation-section",
        description: "What everyday enjoyment actually looks like once the move becomes real life.",
      },
    ],
    []
  );

  if (mode === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading country details…</p>
        </div>
      </div>
    );
  }

  if (mode !== "full" || !fullContent || !activeImage) {
    return <CountryPreviewPage countryKey={countryKey} />;
  }

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid={`country-explore-page-${fullContent.slug}`}>
      <Header />
      <main>
        <section className="border-b border-stone-200/80 bg-[linear-gradient(180deg,#f8f6f0_0%,#fdfcf8_50%,#faf7f2_100%)]">
          <div className="rc-container py-10 sm:py-14 lg:py-16">
            <div className="grid gap-8 lg:grid-cols-[1.02fr_0.98fr] lg:items-center">
              <div className="space-y-6" data-testid="country-explore-hero-copy">
                <div className="inline-flex items-center gap-2 rounded-full border border-stone-200 bg-white/90 px-4 py-2 text-xs font-medium uppercase tracking-[0.24em] text-stone-500" data-testid="country-explore-kicker">
                  Explore Country
                </div>
                <div className="space-y-4">
                  <div className="flex flex-wrap items-center gap-3">
                    <CountryLabel
                      code={fullContent.code}
                      name={fullContent.name}
                      size={22}
                      className="text-stone-900"
                      textClassName="font-manrope text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight"
                      flagTestId={`country-explore-flag-${fullContent.code}`}
                      dataTestId={`country-explore-title-${fullContent.slug}`}
                    />
                  </div>
                  <p className="text-base text-stone-600 sm:text-lg" data-testid="country-explore-subtitle">
                    {fullContent.subtitle}
                  </p>
                  <p className="max-w-2xl text-base leading-8 text-stone-700" data-testid="country-explore-intro">
                    {fullContent.intro}
                  </p>
                  <CountrySafetySignal
                    safetyProfile={safetyProfile || currentPlanCountry?.safety_profile}
                    variant="callout"
                    showContext
                    dataTestIdPrefix={`country-explore-safety-signal-${fullContent.code}`}
                  />
                  <div className="inline-flex max-w-2xl items-start gap-3 rounded-[1.3rem] border border-stone-200 bg-white/85 px-4 py-3 text-sm leading-7 text-stone-600" data-testid="country-explore-pdf-relationship-note">
                    <CircleAlert className="mt-1 h-4 w-4 shrink-0 text-amber-600" />
                    <p>{fullContent.pdfRelationshipNote}</p>
                  </div>
                </div>

              </div>

              <div className="rounded-[2rem] border border-stone-200/90 bg-white/92 p-4 shadow-[0_20px_50px_rgba(28,25,23,0.08)]" data-testid="country-explore-hero-carousel-shell">
                <div className="relative overflow-hidden rounded-[1.65rem] border border-stone-200/90 bg-stone-100">
                  <div className="aspect-[4/3] w-full">
                    <img
                      src={activeImage.url}
                      alt={activeImage.alt}
                      className="h-full w-full object-cover"
                      data-testid={`country-explore-image-${currentSlide + 1}`}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={goToPreviousSlide}
                    className="absolute left-4 bottom-4 inline-flex h-9 w-9 items-center justify-center rounded-full border border-stone-200 bg-white/95 text-stone-900 transition-[background-color,border-color,transform] duration-200 hover:-translate-y-0.5 hover:border-stone-300 hover:bg-white"
                    data-testid="country-explore-carousel-prev"
                    aria-label="Previous image"
                  >
                    ←
                  </button>
                  <button
                    type="button"
                    onClick={goToNextSlide}
                    className="absolute right-4 bottom-4 inline-flex h-9 w-9 items-center justify-center rounded-full border border-stone-200 bg-white/95 text-stone-900 transition-[background-color,border-color,transform] duration-200 hover:-translate-y-0.5 hover:border-stone-300 hover:bg-white"
                    data-testid="country-explore-carousel-next"
                    aria-label="Next image"
                  >
                    →
                  </button>
                </div>

                <div className="flex items-center justify-between gap-3 px-2 pt-4">
                  <span className="rounded-full border border-stone-200 bg-stone-50 px-3 py-1 text-xs font-medium uppercase tracking-[0.18em] text-stone-500" data-testid={`country-explore-image-label-${currentSlide + 1}`}>
                    {activeImage.label}
                  </span>
                  <span className="text-xs text-stone-500" data-testid="country-explore-image-position">
                    {currentSlide + 1} / {fullContent.images.length}
                  </span>
                </div>

                <div className="mt-4 flex items-center gap-2 px-2" data-testid="country-explore-carousel-dots">
                  {fullContent.images.map((image, index) => (
                    <button
                      key={image.url}
                      type="button"
                      onClick={() => setCurrentSlide(index)}
                      className={`h-2.5 rounded-full transition-[width,background-color] duration-200 ${currentSlide === index ? "w-8 bg-stone-900" : "w-2.5 bg-stone-300 hover:bg-stone-400"}`}
                      data-testid={`country-explore-carousel-dot-${index + 1}`}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="rc-home-section pt-8 sm:pt-10">
          <div className="rc-container pb-4" data-testid="country-explore-decision-layer-intro">
            <h2 className="font-manrope text-3xl font-semibold text-stone-900 sm:text-4xl" data-testid="country-explore-start-here-heading">Start Here</h2>
            <p className="max-w-3xl text-base leading-8 text-stone-600">
              Start with why {fullContent.name} is still in the picture, then look at what makes it different, where the main tradeoffs sit, and what you may want to verify next.
            </p>
          </div>
        </section>

        <section className="rc-home-section pt-0">
          <div className="rc-container">
            <Card className="rc-subtle-card border-stone-200/85 bg-white/90" data-testid="country-explore-why-appeared-section">
              <CardHeader className="p-7 pb-5">
                <CardTitle className="font-manrope text-3xl text-stone-900">
                  {hasPlanCountryMatch ? `Why ${fullContent.name} Is Still in Your Blueprint` : `Where ${fullContent.name} May Still Fit`}
                </CardTitle>
                <CardDescription className="text-sm leading-7 text-stone-600">
                  {hasPlanCountryMatch
                    ? `${fullContent.name} remained in the Blueprint for a reason. Start here to see what kept it in view once your priorities and tradeoffs were weighed together.`
                    : `${fullContent.name} may still deserve a serious look. Start here to see where it holds up once the decision becomes more practical.`}
                </CardDescription>
              </CardHeader>
              <CardContent className="grid gap-6 px-7 pb-7 pt-0 lg:grid-cols-[1.15fr_0.85fr]">
                <div className="space-y-5">
                  <p className="text-base leading-8 text-stone-700" data-testid="country-explore-why-appeared-summary">
                    {personalizedSummary.summary}
                  </p>
                  <div className="rounded-[1.5rem] border border-stone-200 bg-stone-50/85 p-5" data-testid="country-explore-why-appeared-support-lines">
                    <p className="text-xs font-semibold uppercase tracking-wide text-stone-500 mb-3">Why this country may work well for you</p>
                    <ul className="space-y-3 text-sm leading-7 text-stone-700">
                      {fitBullets.slice(0, 4).map((line, index) => (
                        <li key={line} className="flex items-start gap-3" data-testid={`country-explore-support-line-${index + 1}`}>
                          <ShieldCheck className="mt-1 h-4 w-4 shrink-0 text-amber-600" />
                          <span>{line}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="rounded-[1.6rem] border border-amber-200/80 bg-amber-50/80 p-5" data-testid="country-explore-why-appeared-caution-box">
                    <p className="text-xs font-semibold uppercase tracking-wide text-amber-700 mb-2">Meaningful caution</p>
                    <p className="text-sm leading-7 text-stone-700">{cautionText}</p>
                  </div>
                  <div className="rounded-[1.4rem] border border-stone-200 bg-white/80 p-4" data-testid="country-explore-live-context-note">
                    <p className="text-xs font-semibold uppercase tracking-wide text-stone-500 mb-2">Bring this back to the bigger decision</p>
                    <p className="text-sm leading-7 text-stone-600">
                      Let this country stand on its own for a moment, then bring it back into your shortlist once you are clearer on where it genuinely holds up.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="rc-home-section pt-0">
          <div className="rc-container space-y-5">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <p className="rc-section-kicker">Country Snapshot</p>
                <h2 className="rc-section-title text-left">What stands out about {fullContent.name}</h2>
              </div>
              <span className="rounded-full border border-stone-200 bg-stone-50 px-4 py-2 text-xs font-medium uppercase tracking-[0.18em] text-stone-500" data-testid="country-explore-reviewed-label">
                {reviewedLabel}
              </span>
            </div>
            <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3" data-testid="country-explore-scorecard-grid">
              {fullContent.scorecard.map((signal) => {
                const Icon = scorecardIcons[signal.label] || Globe2;
                return (
                  <Card key={signal.label} className="rc-subtle-card border-stone-200/85 bg-white/90">
                    <CardHeader className="space-y-4 p-6">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex h-11 w-11 items-center justify-center rounded-full border border-amber-200 bg-amber-50 text-amber-500">
                          <Icon className="h-5 w-5" />
                        </div>
                        <span className="rounded-full border border-stone-200 bg-stone-50 px-3 py-1 text-xs font-medium uppercase tracking-[0.18em] text-stone-500" data-testid={`country-explore-scorecard-signal-${signal.label}`}>
                          {signal.signal}
                        </span>
                      </div>
                      <div className="space-y-2">
                        <CardTitle className="font-manrope text-xl text-stone-900">{signal.label}</CardTitle>
                        <CardDescription className="text-sm leading-7 text-stone-600">{signal.detail}</CardDescription>
                      </div>
                    </CardHeader>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        <section className="rc-home-section pt-0">
          <div className="rc-container">
            <Card className="rc-subtle-card border-stone-200/85 bg-white/90" data-testid="country-explore-tradeoffs-section">
              <CardHeader className="p-7 pb-5">
                <CardTitle className="font-manrope text-2xl text-stone-900">What could make this harder?</CardTitle>
                <CardDescription className="text-sm leading-7 text-stone-600">
                  These are the friction points most likely to change the fit once {fullContent.name} moves from interesting to realistic.
                </CardDescription>
              </CardHeader>
              <CardContent className="grid gap-5 px-7 pb-7 pt-0 md:grid-cols-2">
                <ul className="space-y-3 text-sm leading-7 text-stone-700">
                  {tradeoffBullets.slice(0, 2).map((item, index) => (
                    <li key={item} className="flex items-start gap-3" data-testid={`country-explore-tradeoff-bullet-${index + 1}`}>
                      <span className="mt-2 h-2 w-2 rounded-full bg-amber-500" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <ul className="space-y-3 text-sm leading-7 text-stone-700">
                  {tradeoffBullets.slice(2).map((item, index) => (
                    <li key={item} className="flex items-start gap-3" data-testid={`country-explore-tradeoff-bullet-${index + 3}`}>
                      <span className="mt-2 h-2 w-2 rounded-full bg-amber-500" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="rc-home-section pt-0">
          <div className="rc-container space-y-6">
            <div className="max-w-2xl space-y-3" data-testid="country-explore-regions-header">
              <p className="rc-section-kicker">Where to focus first</p>
              <h2 className="rc-section-title text-left">Where should you focus first?</h2>
              <p className="text-base leading-8 text-stone-600">
                Different parts of the country can lead to very different living experiences. Start where the daily rhythm, service access, and housing reality are most likely to match your priorities.
              </p>
            </div>
            <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3" data-testid="country-explore-regions-grid">
              {fullContent.regions.map((region, index) => {
                const profileHint = getRegionProfileHint(countryKey, region.name, plan?.summary || {});
                return (
                  <Card key={region.name} className="rc-subtle-card flex h-full flex-col border-stone-200/85 bg-white/90">
                    <CardHeader className="space-y-4 p-6 pb-4">
                      <div className="flex h-11 w-11 items-center justify-center rounded-full border border-stone-200 bg-stone-50 text-stone-700">
                        <MapPinned className="h-5 w-5" />
                      </div>
                      <CardTitle className="font-manrope text-xl text-stone-900" data-testid={`country-explore-region-name-${index + 1}`}>{region.name}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4 px-6 pb-6 pt-0">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-stone-500 mb-1">Known for</p>
                        <p className="text-sm leading-7 text-stone-700" data-testid={`country-explore-region-known-for-${index + 1}`}>{region.knownFor}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-stone-500 mb-1">Tends to suit</p>
                        <p className="text-sm leading-7 text-stone-700" data-testid={`country-explore-region-suits-${index + 1}`}>{region.suits}</p>
                      </div>
                      {profileHint ? (
                        <p className="rounded-[1.2rem] border border-amber-200 bg-amber-50/70 px-4 py-3 text-sm leading-7 text-stone-700" data-testid={`country-explore-region-profile-hint-${index + 1}`}>
                          {profileHint}
                        </p>
                      ) : null}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        <section className="rc-home-section pt-0">
          <div className="rc-container">
            <Card className="rc-subtle-card border-stone-200/85 bg-white/90" data-testid="country-explore-residency-section">
              <CardHeader className="p-7 pb-5">
                <CardTitle className="font-manrope text-2xl text-stone-900">How residency may work here</CardTitle>
                <CardDescription className="text-sm leading-7 text-stone-600">
                  Residency often changes the answer. Use this section to see whether the country still works once paperwork, timing, and likely pathways become real.
                </CardDescription>
              </CardHeader>
              <CardContent className="grid gap-5 px-7 pb-7 pt-0 lg:grid-cols-2">
                {fullContent.residencyPathways.map((pathway, index) => (
                  <div key={pathway.name} className="rounded-[1.5rem] border border-stone-200 bg-stone-50/85 p-5" data-testid={`country-explore-residency-card-${index + 1}`}>
                    <p className="font-manrope text-lg text-stone-900 mb-2">{pathway.name}</p>
                    <div className="space-y-3 text-sm leading-7 text-stone-700">
                      <p><span className="font-semibold text-stone-900">Who it tends to suit:</span> {pathway.suits}</p>
                      <p><span className="font-semibold text-stone-900">Broad proof themes:</span> {pathway.proofThemes}</p>
                      <p><span className="font-semibold text-stone-900">Practical friction:</span> {pathway.friction}</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="rc-home-section pt-0" data-testid="country-explore-practical-context-group">
          <div className="rc-container">
            <Card className="rc-subtle-card border-stone-200/85 bg-white/90">
              <CardHeader className="p-7 pb-4">
                <p className="rc-section-kicker">Daily life and setup</p>
                <CardTitle className="font-manrope text-2xl text-stone-900">What may matter once the move becomes real?</CardTitle>
                <CardDescription className="max-w-3xl text-sm leading-7 text-stone-600">
                  {fullContent.practicalContextIntro}
                </CardDescription>
              </CardHeader>
              <CardContent className="px-7 pb-7 pt-0">
                <Accordion type="multiple" className="space-y-3" data-testid="country-explore-practical-context-accordion">
                  <AccordionItem value="cost" className="rounded-[1.4rem] border border-stone-200 bg-stone-50/75 px-5" data-testid="country-explore-cost-section">
                    <AccordionTrigger className="py-5 no-underline hover:no-underline" data-testid="country-explore-cost-trigger">
                      <div>
                        <p className="font-manrope text-lg text-stone-900 text-left">What daily costs may look like</p>
                        <p className="text-sm leading-7 text-stone-600 text-left">Broad budget ranges, rent pressure, and where the value picture shifts first.</p>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-5 pt-2">
                        <div className="grid gap-4 md:grid-cols-3">
                          {fullContent.costOverview.budgetBands.map((band, index) => (
                            <div key={band.label} className="rounded-[1.25rem] border border-stone-200 bg-white/85 p-4" data-testid={`country-explore-budget-band-${index + 1}`}>
                              <p className="text-xs font-semibold uppercase tracking-wide text-stone-500 mb-2">{band.label}</p>
                              <p className="font-manrope text-xl text-stone-900 mb-2">
                                {getCountryCostRangePresentation(fullContent.code, band.range).displayRange}
                              </p>
                              <p className="text-sm leading-7 text-stone-600">{band.detail}</p>
                            </div>
                          ))}
                        </div>
                        <ul className="space-y-3 text-sm leading-7 text-stone-700">
                          {fullContent.costOverview.bullets.map((item, index) => (
                            <li key={item} className="flex items-start gap-3" data-testid={`country-explore-cost-bullet-${index + 1}`}>
                              <span className="mt-2 h-2 w-2 rounded-full bg-stone-400" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="healthcare" className="rounded-[1.4rem] border border-stone-200 bg-stone-50/75 px-5" data-testid="country-explore-healthcare-section">
                    <AccordionTrigger className="py-5 no-underline hover:no-underline" data-testid="country-explore-healthcare-trigger">
                      <div>
                        <p className="font-manrope text-lg text-stone-900 text-left">What healthcare may feel like</p>
                        <p className="text-sm leading-7 text-stone-600 text-left">How the public/private reality usually works and what that may mean in everyday life.</p>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <ul className="space-y-3 pt-2 text-sm leading-7 text-stone-700">
                        {fullContent.healthcareOverview.map((item, index) => (
                          <li key={item} className="flex items-start gap-3" data-testid={`country-explore-healthcare-bullet-${index + 1}`}>
                            <HeartPulse className="mt-1 h-4 w-4 text-emerald-600" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="daily-life" className="rounded-[1.4rem] border border-stone-200 bg-stone-50/75 px-5" data-testid="country-explore-daily-life-section">
                    <AccordionTrigger className="py-5 no-underline hover:no-underline" data-testid="country-explore-daily-life-trigger">
                      <div>
                        <p className="font-manrope text-lg text-stone-900 text-left">What daily life may feel like</p>
                        <p className="text-sm leading-7 text-stone-600 text-left">Pace of life, social texture, language reality, and everyday mobility once this becomes your regular life.</p>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <ul className="space-y-3 pt-2 text-sm leading-7 text-stone-700">
                        {fullContent.dailyLifeOverview.map((item, index) => (
                          <li key={item} className="flex items-start gap-3" data-testid={`country-explore-daily-life-bullet-${index + 1}`}>
                            <Waves className="mt-1 h-4 w-4 text-sky-600" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="infrastructure" className="rounded-[1.4rem] border border-stone-200 bg-stone-50/75 px-5" data-testid="country-explore-infrastructure-section">
                    <AccordionTrigger className="py-5 no-underline hover:no-underline" data-testid="country-explore-infrastructure-trigger">
                      <div>
                        <p className="font-manrope text-lg text-stone-900 text-left">Internet and everyday infrastructure</p>
                        <p className="text-sm leading-7 text-stone-600 text-left">Remote-work practicality, service reliability, and where daily logistics may feel smoothest.</p>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <ul className="space-y-3 pt-2 text-sm leading-7 text-stone-700">
                        {fullContent.internetOverview.map((item, index) => (
                          <li key={item} className="flex items-start gap-3" data-testid={`country-explore-infrastructure-bullet-${index + 1}`}>
                            <Wifi className="mt-1 h-4 w-4 text-amber-600" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="practical-setup" className="rounded-[1.4rem] border border-stone-200 bg-stone-50/75 px-5" data-testid="country-explore-practical-setup-section">
                    <AccordionTrigger className="py-5 no-underline hover:no-underline" data-testid="country-explore-practical-setup-trigger">
                      <div>
                        <p className="font-manrope text-lg text-stone-900 text-left">What setup usually involves</p>
                        <p className="text-sm leading-7 text-stone-600 text-left">Banking, tax awareness, onboarding friction, and the parts of the move that usually need more care.</p>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <ul className="space-y-3 pt-2 text-sm leading-7 text-stone-700">
                        {fullContent.practicalSetup.map((item, index) => (
                          <li key={item} className="flex items-start gap-3" data-testid={`country-explore-practical-setup-bullet-${index + 1}`}>
                            <CreditCard className="mt-1 h-4 w-4 text-stone-700" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="recreation" className="rounded-[1.4rem] border border-stone-200 bg-stone-50/75 px-5" data-testid="country-explore-recreation-section">
                    <AccordionTrigger className="py-5 no-underline hover:no-underline" data-testid="country-explore-recreation-trigger">
                      <div>
                        <p className="font-manrope text-lg text-stone-900 text-left">What everyday enjoyment may look like</p>
                        <p className="text-sm leading-7 text-stone-600 text-left">What everyday enjoyment may actually look like once the move becomes ordinary life.</p>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <ul className="space-y-3 pt-2 text-sm leading-7 text-stone-700">
                        {fullContent.recreationOverview.map((item, index) => (
                          <li key={item} className="flex items-start gap-3" data-testid={`country-explore-recreation-bullet-${index + 1}`}>
                            <SunMedium className="mt-1 h-4 w-4 text-amber-600" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="rc-home-section pt-0" data-testid="country-explore-references-group">
          <div className="rc-container">
            <Card className="rc-subtle-card border-stone-200/85 bg-white/90">
              <CardHeader className="p-7 pb-4">
                <p className="rc-section-kicker">References</p>
                <CardTitle className="font-manrope text-2xl text-stone-900">What should you verify next?</CardTitle>
                <CardDescription className="max-w-3xl text-sm leading-7 text-stone-600">
                  {fullContent.referencesIntro}
                </CardDescription>
                <div className="inline-flex max-w-3xl items-start gap-3 rounded-[1.2rem] border border-stone-200 bg-stone-50/80 px-4 py-3 text-sm leading-7 text-stone-600" data-testid="country-explore-references-trust-copy">
                  <CircleAlert className="mt-1 h-4 w-4 shrink-0 text-amber-600" />
                  <p>Use official sources to verify current pathway requirements before acting. The outside links here are practical starting points and may change over time.</p>
                </div>
              </CardHeader>
              <CardContent className="px-7 pb-7 pt-0">
                <Accordion type="multiple" className="space-y-3" data-testid="country-explore-references-accordion">
                  <AccordionItem value="official-resources" className="rounded-[1.4rem] border border-stone-200 bg-stone-50/75 px-5" data-testid="country-explore-resources-section">
                    <AccordionTrigger className="py-5 no-underline hover:no-underline" data-testid="country-explore-resources-trigger">
                      <div>
                        <p className="font-manrope text-lg text-stone-900 text-left">Official sources to check</p>
                        <p className="text-sm leading-7 text-stone-600 text-left">A small, official-first set of sources for checking requirements and practical next steps.</p>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="grid gap-4 pt-2 md:grid-cols-2">
                        {fullContent.resources.filter((resource) => resource.active !== false).map((resource, index) => (
                          <a
                            key={resource.title}
                            href={resource.href}
                            target="_blank"
                            rel="noreferrer"
                            className="rounded-[1.3rem] border border-stone-200 bg-white/85 p-4 transition-[border-color,transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:border-stone-300 hover:shadow-[0_12px_28px_rgba(28,25,23,0.06)]"
                            data-testid={`country-explore-resource-link-${index + 1}`}
                          >
                            <div className="flex items-start justify-between gap-4">
                              <div>
                                <div className="flex flex-wrap items-center gap-2 mb-2">
                                  <p className="font-manrope text-lg text-stone-900">{resource.title}</p>
                                  <span className="rounded-full border border-stone-200 bg-stone-50 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-stone-500">
                                    {resource.sourceType}
                                  </span>
                                </div>
                                <p className="text-sm leading-7 text-stone-600">{resource.description}</p>
                                <p className="mt-2 text-xs text-stone-400">Last reviewed {resource.lastReviewed || currentReviewedMonthYear}</p>
                              </div>
                              <ExternalLink className="h-4 w-4 text-stone-500" />
                            </div>
                          </a>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="optional-exploration" className="rounded-[1.4rem] border border-stone-200 bg-stone-50/75 px-5" data-testid="country-explore-watch-more-section">
                    <AccordionTrigger className="py-5 no-underline hover:no-underline" data-testid="country-explore-watch-more-trigger">
                      <div>
                        <p className="font-manrope text-lg text-stone-900 text-left">One outside perspective</p>
                        <p className="text-sm leading-7 text-stone-600 text-left">One extra outside lens if you want to compare the guidance here with grounded day-to-day perspectives.</p>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="grid gap-4 pt-2">
                        {fullContent.watchMore.filter((item) => item.active !== false).map((item, index) => (
                          <div key={item.title} className="rounded-[1.3rem] border border-stone-200 bg-white/85 p-4" data-testid={`country-explore-watch-more-card-${index + 1}`}>
                            <div className="flex items-start gap-3">
                              <PlayCircle className="mt-1 h-5 w-5 text-amber-600" />
                              <div className="space-y-3">
                                <div>
                                  <div className="flex flex-wrap items-center gap-2 mb-2">
                                    <p className="font-manrope text-lg text-stone-900">{item.title}</p>
                                    <span className="rounded-full border border-stone-200 bg-stone-50 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-stone-500">
                                      {item.sourceType}
                                    </span>
                                  </div>
                                  <p className="text-sm leading-7 text-stone-600">{item.description}</p>
                                </div>
                                <a
                                  href={item.href}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-flex items-center gap-2 text-sm font-medium text-amber-700 transition-colors hover:text-amber-800 hover:underline"
                                  data-testid={`country-explore-watch-more-link-${index + 1}`}
                                >
                                  {item.ctaLabel}
                                  <ExternalLink className="h-4 w-4" />
                                </a>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="rc-home-section-ending pt-0">
          <div className="rc-container">
            <div className="rounded-[2rem] border border-stone-200/90 bg-[linear-gradient(135deg,rgba(251,250,247,0.96)_0%,rgba(247,243,235,0.96)_100%)] p-8 shadow-[0_16px_34px_rgba(28,25,23,0.06)] sm:p-10" data-testid="country-explore-end-cta">
              <div className="max-w-3xl space-y-4">
                <p className="rc-section-kicker">{fullContent.endCta.eyebrow}</p>
                <h2 className="font-manrope text-3xl text-stone-900 sm:text-4xl" data-testid="country-explore-end-cta-title">
                  {fullContent.endCta.title}
                </h2>
                <p className="text-base leading-8 text-stone-600" data-testid="country-explore-end-cta-description">
                  {fullContent.endCta.description}
                </p>
              </div>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Button asChild className="h-12 rounded-full bg-stone-950 px-7 text-sm font-medium text-stone-50 transition-[background-color,transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:bg-black" data-testid="country-explore-end-cta-primary">
                  <Link to={fullContent.endCta.primaryHref}>
                    {fullContent.endCta.primaryLabel}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                {fullContent.endCta.secondaryLabel && fullContent.endCta.secondaryHref ? (
                  <Button asChild variant="outline" className="rc-button-outline h-12 rounded-full px-7 text-sm" data-testid="country-explore-end-cta-secondary">
                    <Link to={fullContent.endCta.secondaryHref}>{fullContent.endCta.secondaryLabel}</Link>
                  </Button>
                ) : null}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default CountryExploreOrPreviewPage;